import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { io } from "socket.io-client";
import { useQueryClient } from "react-query";
import { GlobalContext } from "../context/Provider";

export const ChatSocketContext = createContext({ socket: null, connected: false });
const defaultApiUrl =
  process.env.NODE_ENV === "production"
    ? `${window.location.origin}/api`
    : "http://localhost:5070/api";
const apiUrl = process.env.REACT_APP_API_URL || defaultApiUrl;
const socketUrl = process.env.REACT_APP_SOCKET_URL || apiUrl.replace(/\/api\/?$/, "");

const ChatSocketProvider = ({ children }) => {
  const { authState } = useContext(GlobalContext);
  const queryClient = useQueryClient();
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!authState.isAuthenticated || !authState.accessToken) {
      setSocket((current) => {
        current?.disconnect();
        return null;
      });
      setConnected(false);
      return undefined;
    }

    const connection = io(socketUrl, {
      auth: { token: authState.accessToken },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelayMax: 10000,
    });
    const refreshChat = () => {
      queryClient.invalidateQueries("chatConversations");
      queryClient.invalidateQueries("chatUnreadCount");
    };
    const refreshMessages = (message) => {
      refreshChat();
      if (message?.conversation) {
        queryClient.invalidateQueries(["chatMessages", String(message.conversation)]);
      }
    };
    connection.on("connect", () => setConnected(true));
    connection.on("disconnect", () => setConnected(false));
    connection.on("message:new", refreshMessages);
    connection.on("conversation:updated", refreshChat);
    connection.on("conversation:read", (payload) => {
      refreshChat();
      if (payload?.conversationId) {
        queryClient.invalidateQueries(["chatMessages", String(payload.conversationId)]);
      }
    });
    setSocket(connection);

    return () => {
      connection.removeAllListeners();
      connection.disconnect();
      setConnected(false);
      setSocket(null);
    };
  }, [authState.isAuthenticated, authState.accessToken, queryClient]);

  const value = useMemo(() => ({ socket, connected }), [socket, connected]);
  return <ChatSocketContext.Provider value={value}>{children}</ChatSocketContext.Provider>;
};

export const useChatSocket = () => useContext(ChatSocketContext);
export default ChatSocketProvider;
